/**
 * Belge yükleme modal'ı.
 * Kamera + galeri + manuel dosya seç. JPG/PNG/PDF, max 10 MB.
 */

import { DatePicker } from '@/components/ui/DatePicker'
import { useEffect, useRef, useState } from 'react'
import { Upload, FileText, X, Camera, Image as ImageIcon } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/contexts/AuthContext'
import {
  belgeYukle,
  BELGE_TIPI_ETIKETLERI,
  type BelgeTipi,
} from '@/lib/services/belge-service'
import type { KullaniciPersonel, IsoTarih } from '@/types'

interface Props {
  acik: boolean
  personel: KullaniciPersonel | null
  onKapat: () => void
  onYuklendi: () => void
}

export function BelgeYukleModal({ acik, personel, onKapat, onYuklendi }: Props) {
  const { firebaseUser } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)

  const [tipi, setTipi] = useState<BelgeTipi>('kimlikOn')
  const [ozelTip, setOzelTip] = useState('')
  const [gecerlilik, setGecerlilik] = useState('')
  const [dosya, setDosya] = useState<File | null>(null)
  const [onizleme, setOnizleme] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    if (!acik) return
    setTipi('kimlikOn')
    setOzelTip('')
    setGecerlilik('')
    setDosya(null)
    setOnizleme(null)
    setHata(null)
  }, [acik])

  function dosyaSec(file: File | null) {
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setHata('Dosya 10 MB\'dan büyük olamaz')
      return
    }
    const okMime = ['image/jpeg', 'image/png', 'application/pdf']
    if (!okMime.includes(file.type)) {
      setHata('Sadece JPG, PNG veya PDF kabul edilir')
      return
    }
    setDosya(file)
    setHata(null)

    // Önizleme (resim için)
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => setOnizleme(e.target?.result as string)
      reader.readAsDataURL(file)
    } else {
      setOnizleme(null)
    }
  }

  async function yukle() {
    if (!dosya || !personel || !firebaseUser) return
    if (tipi === 'diger' && !ozelTip.trim()) {
      setHata('"Diğer" için açıklama yaz')
      return
    }
    setYukleniyor(true)
    setHata(null)
    try {
      await belgeYukle(
        {
          personelId: personel.id,
          tipi,
          ozelTip: tipi === 'diger' ? ozelTip : undefined,
          gecerlilikTarihi: gecerlilik ? (gecerlilik as IsoTarih) : undefined,
          dosya,
        },
        firebaseUser.uid
      )
      onYuklendi()
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }

  if (!personel) return null

  const boyutKb = dosya ? (dosya.size / 1024).toFixed(0) : null

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Belge Yükle" boyut="md">
      <div className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm">
          <p className="font-semibold text-brand-900">{personel.isim}</p>
        </div>

        {/* Belge tipi */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Belge Tipi <span className="text-red-500">*</span>
          </label>
          <select
            value={tipi}
            onChange={(e) => setTipi(e.target.value as BelgeTipi)}
            className="input-base"
          >
            {(Object.keys(BELGE_TIPI_ETIKETLERI) as BelgeTipi[]).map((t) => (
              <option key={t} value={t}>{BELGE_TIPI_ETIKETLERI[t]}</option>
            ))}
          </select>
        </div>

        {tipi === 'diger' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Açıklama <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={ozelTip}
              onChange={(e) => setOzelTip(e.target.value)}
              className="input-base"
              placeholder="Örn: SGK belgesi"
            />
          </div>
        )}

        {/* Geçerlilik tarihi (opsiyonel) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Geçerlilik Tarihi (opsiyonel)
          </label>
          <DatePicker
            value={gecerlilik}
            onChange={setGecerlilik}
            placeholder="Geçerlilik tarihi seç"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Süresi yaklaşırken bildirim alacaksın
          </p>
        </div>

        {/* Dosya seçimi */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dosya <span className="text-red-500">*</span>
          </label>

          {!dosya ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors"
              >
                <Camera className="w-6 h-6 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Kamera</span>
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-brand-500 hover:bg-brand-50 transition-colors"
              >
                <ImageIcon className="w-6 h-6 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Galeri / Dosya</span>
              </button>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl p-3">
              <div className="flex items-start gap-3">
                {onizleme ? (
                  <img
                    src={onizleme}
                    alt="Önizleme"
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900 truncate">{dosya.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{boyutKb} KB</p>
                  <button
                    type="button"
                    onClick={() => { setDosya(null); setOnizleme(null) }}
                    className="text-xs text-red-600 hover:underline mt-1 inline-flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Kaldır
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hidden inputs */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => dosyaSec(e.target.files?.[0] ?? null)}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            className="hidden"
            onChange={(e) => dosyaSec(e.target.files?.[0] ?? null)}
          />

          <p className="text-[11px] text-gray-500 mt-1.5">
            JPG, PNG veya PDF • max 10 MB
          </p>
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onKapat}
            disabled={yukleniyor}
            className="btn-secondary flex-1"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={yukle}
            disabled={yukleniyor || !dosya}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {yukleniyor ? 'Yükleniyor...' : 'Yükle'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
